import { API_DOMAIN } from "@monorepo/constants";
import { modifyUrlForHistoricalPayment } from "@monorepo/services/helpers/modifyUrlForHistoricalPayment";
import {
  IAddServiceToCartPayload,
  IAddServiceToCartResponse,
  IAddTemplateResponse,
  IAddTemplateToCartResponse,
  ICart,
  ICartGroup,
  IClient,
  ICreateCartGroupResponse,
  IEditCartPermissions,
  IEditTemplatePayload,
  IGenericSuccessResponse,
  IGetCartByIdResponse,
  IHistoricalPaymentSliceState,
  IOrder,
  IPaynowTemplateResponse,
  IRecurringPayment,
  IRecurringPaymentPayload,
  IShareCartGenericPayload,
  IShareCartPayload,
  IShareCartRefuseResponse,
  ITemplate,
  ITemplatePayload,
  ITemplatePayloadBase,
  IUpdateCartPayload,
  IUpdateCartResponse,
} from "@monorepo/types";
import { createApi } from "@reduxjs/toolkit/query/react";

import { customBaseQuery } from "../../helpers/baseQuery";

export const cartApi = createApi({
  reducerPath: "cartApi",
  baseQuery: customBaseQuery<{
    data: Array<ICart> | ICart | ICartGroup | Array<ICartGroup> | IGetCartByIdResponse | IRecurringPayment;
    meta: IGenericSuccessResponse;
  }>({
    baseUrl: API_DOMAIN,
  }),
  tagTypes: ["Carts", "CartGroup", "CartGroupById", "RecurringPayments", "Templates", "SharedClients"],
  endpoints: (builder) => ({
    deleteCart: builder.mutation<void, { cartId: string; cartGroupId: string }>({
      query: ({ cartId }) => ({
        url: `/api/v1/cart/${cartId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, error, { cartGroupId }) => {
        if (!error) {
          return ["Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),
    payCashMock: builder.mutation<IOrder, string>({
      query: (id) => ({
        url: `/api/v1/order/${id}/paid`,
        method: "PUT",
      }),
      invalidatesTags: (result) => {
        if (result) {
          return ["Carts", "CartGroup"];
        } else return [];
      },
    }),
    deleteRecurringPayment: builder.mutation<void, { recurringPaymentId: string; cartGroupId: string }>({
      query: ({ recurringPaymentId }) => ({
        url: `/api/v1/recurring-payment/${recurringPaymentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, error, { cartGroupId }) => {
        if (!error) {
          return ["RecurringPayments", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),
    deleteTemplate: builder.mutation<void, { templateId: string; cartGroupId: number }>({
      query: ({ templateId }) => ({
        url: `/api/v1/templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, error, { cartGroupId }) => {
        if (!error) {
          return ["Templates", "Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),

    updateCart: builder.mutation<IUpdateCartResponse, IUpdateCartPayload>({
      queryFn: async ({ cartId, ...rest }, api, _, baseQuery) => {
        const state = api.getState() as IHistoricalPaymentSliceState;
        const url = modifyUrlForHistoricalPayment(state, `/api/v1/cart/${cartId}`);

        const resp = await baseQuery({
          url: url,
          method: "PUT",
          body: rest,
        });

        return resp as any;
      },
      invalidatesTags: (_, error, { cart_group_id: cartGroupId }) => {
        if (!error) {
          return ["Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),

    createCartGroup: builder.mutation<ICreateCartGroupResponse, string>({
      query: (name: string) => ({
        url: "/api/v1/cart-group",
        method: "POST",
        body: {
          name,
        },
      }),
      invalidatesTags: ["CartGroup"],
    }),

    editCartGroup: builder.mutation<ICartGroup, { id: string; name: string }>({
      query: ({ id, name }) => ({
        url: `/api/v1/cart-group/${id}`,
        method: "PUT",
        body: {
          name,
        },
      }),
      invalidatesTags: (_, error) => {
        if (!error) {
          return ["CartGroup"];
        }
        return [];
      },
    }),

    editTemplate: builder.mutation<ITemplate, IEditTemplatePayload>({
      query: (data) => ({
        url: `/api/v1/templates/${data.id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_, error, { cart_group_id: cartGroupId }) => {
        if (!error) {
          return ["Templates", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),

    deleteCartGroup: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/v1/cart-group/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CartGroup"],
    }),

    addTemplateToCart: builder.mutation<IAddTemplateToCartResponse, ITemplatePayload & { id: string }>({
      query: (data) => ({
        url: `/api/v1/cart/add-template-to-cart/${data.id}?cart_group_id=${data.cart_group_id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, error, { cart_group_id: cartGroupId }) => {
        if (!error) {
          return ["Templates", "CartGroup", "Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),

    addRecurringPayment: builder.mutation<IRecurringPayment, IRecurringPaymentPayload>({
      query: ({ addToCart = false, ...data }) => ({
        url: "/api/v1/recurring-payment",
        method: "POST",
        body: {
          add_to_cart: addToCart,
          ...data,
        },
      }),
      invalidatesTags: (_, error, { cart_group_id: cartGroupId }) => {
        if (!error) {
          return ["RecurringPayments", "CartGroup", "Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
      transformResponse: (res) => {
        return res.data as IRecurringPayment;
      },
    }),

    paynowTemplate: builder.mutation<IPaynowTemplateResponse, { template_id: string; cart_group_id: string }>({
      query: (data) => ({
        url: `/api/v1/templates/pay-now?cart_group_id=${data.cart_group_id}&template_id=${data.template_id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, error, { cart_group_id: cartGroupId }) => {
        if (!error) {
          return ["RecurringPayments", "CartGroup", "Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),

    addTemplate: builder.mutation<IAddTemplateResponse, ITemplatePayloadBase>({
      query: (data) => ({
        url: "/api/v1/templates",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, error) => {
        if (!error) {
          return ["Templates", "CartGroup", "Carts"];
        }
        return [];
      },
    }),

    addServiceToCart: builder.mutation<IAddServiceToCartResponse, IAddServiceToCartPayload>({
      query: (data) => ({
        url: "/api/v1/cart/add-service-to-cart",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, error, { cart_group_id: cartGroupId }) => {
        if (!error) {
          return ["CartGroup", "Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),

    getCartsByCartGroupId: builder.query<Array<ICart>, string>({
      queryFn: async (cartGroupId, api, _, baseQuery) => {
        const state = api.getState() as IHistoricalPaymentSliceState;
        const url = modifyUrlForHistoricalPayment(state, `/api/v1/cart/by-cart-group/${cartGroupId}`);

        const resp = await baseQuery({
          url: url,
        });

        return resp as any;
      },
      providesTags: (result, _, id) => {
        if (result) {
          return [{ type: "CartGroupById", id }];
        }
        return [];
      },
    }),

    getCartGroupById: builder.query<ICartGroup, string>({
      queryFn: async (id, api, _, baseQuery) => {
        const state = api.getState() as IHistoricalPaymentSliceState;
        const url = modifyUrlForHistoricalPayment(state, `/api/v1/cart-group/${id}`);

        const resp = await baseQuery({
          url: url,
        });

        return resp as any;
      },
      providesTags: (result, _, id) => {
        if (result) {
          return [{ type: "CartGroup", id }];
        }
        return [];
      },
    }),

    delayPayment: builder.mutation<void, { id: string; cartGroupId: string }>({
      query: ({ id }) => ({
        url: `/api/v1/cart/${id}/delay`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, { cartGroupId }) => {
        if (!error) {
          return ["Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),

    undelayPayment: builder.mutation<void, { id: string; cartGroupId: string }>({
      query: ({ id }) => ({
        url: `/api/v1/cart/${id}/undelay`,
        method: "PUT",
      }),

      invalidatesTags: (_, error, { cartGroupId }) => {
        if (!error) {
          return ["Carts", { type: "CartGroupById", id: cartGroupId }];
        }
        return [];
      },
    }),

    getRecurringPayment: builder.query<IRecurringPayment, string>({
      query: (id) => ({
        url: `/api/v1/recurring-payment/${id}`,
      }),
    }),

    getRecurringPayments: builder.query<Array<IRecurringPayment>, void>({
      query: () => ({
        url: "/api/v1/recurring-payment",
      }),
      providesTags: ["RecurringPayments"],
    }),

    getTemplate: builder.query<ITemplate, string>({
      query: (id) => ({
        url: `/api/v1/templates/${id}`,
      }),

      providesTags: ["Templates"],
    }),

    getTemplates: builder.query<Array<ITemplate>, void>({
      query: () => ({
        url: "/api/v1/templates",
      }),
      providesTags: ["Templates"],
    }),

    getCartById: builder.query<IGetCartByIdResponse, string>({
      queryFn: async (id, api, _, baseQuery) => {
        const state = api.getState() as IHistoricalPaymentSliceState;
        const url = modifyUrlForHistoricalPayment(state, `/api/v1/cart/${id}`);

        const resp = await baseQuery({
          url: url,
        });

        return resp as any;
      },
    }),

    reloadCartById: builder.query<IGetCartByIdResponse, string>({
      query: (id) => ({
        url: `/api/v1/cart/${id}/reload`,
      }),
    }),

    getCarts: builder.query<Array<ICart>, void>({
      queryFn: async (args, api, _, baseQuery) => {
        const state = api.getState() as IHistoricalPaymentSliceState;
        const url = modifyUrlForHistoricalPayment(state, "/api/v1/cart");

        const resp = await baseQuery({
          url: url,
        });

        return resp as any;
      },
      providesTags: ["Carts"],
    }),

    getCartGroups: builder.query<Array<ICartGroup>, void>({
      queryFn: async (args, api, _, baseQuery) => {
        const state = api.getState() as IHistoricalPaymentSliceState;
        const url = modifyUrlForHistoricalPayment(state, "/api/v1/cart-group");

        const resp = await baseQuery({
          url: url,
        });

        return resp as any;
      },

      providesTags: ["CartGroup"],
    }),

    getCartGroupsWithTrash: builder.query<Array<ICartGroup>, void>({
      queryFn: async (args, api, _, baseQuery) => {
        const state = api.getState() as IHistoricalPaymentSliceState;
        const url = modifyUrlForHistoricalPayment(state, "/api/v1/cart-group/with-trashed");

        return (await baseQuery({
          url: url,
        })) as any;
      },
    }),

    shareCart: builder.mutation<IGenericSuccessResponse, IShareCartPayload>({
      query: (data) => ({
        url: "/api/v1/share-cart",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { meta: IGenericSuccessResponse }) => {
        return response?.meta;
      },
      invalidatesTags: ["SharedClients", "CartGroup"],
    }),

    shareCartConfirm: builder.mutation<IGenericSuccessResponse, IShareCartGenericPayload>({
      query: (data) => ({
        url: "/api/v1/share-cart/confirm",
        method: "POST",
        body: data,
      }),
      transformResponse: (res) => {
        return res.meta as IGenericSuccessResponse;
      },
      invalidatesTags: ["CartGroup", "Carts"],
    }),

    shareCartRefuse: builder.mutation<IShareCartRefuseResponse, IShareCartGenericPayload>({
      query: (data) => ({
        url: "/api/v1/share-cart/refuse",
        method: "POST",
        body: data,
      }),
      transformResponse: (res) => {
        return res.meta as IShareCartRefuseResponse;
      },
      invalidatesTags: (_, error, { cart_group_id: cartGroupId }) => {
        if (!error) {
          return [{ type: "CartGroup", id: cartGroupId }];
        }
        return [];
      },
    }),

    editSharedCartPermissions: builder.mutation<IGenericSuccessResponse, IEditCartPermissions>({
      query: (data) => ({
        url: "/api/v1/share-cart/update",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { meta: IGenericSuccessResponse }) => {
        return response?.meta;
      },
      invalidatesTags: ["CartGroup", "Carts", "SharedClients"],
    }),

    cancelSharedCart: builder.mutation<IGenericSuccessResponse, IShareCartGenericPayload>({
      query: (data) => ({
        url: "/api/v1/share-cart/cancel",
        method: "POST",
        body: data,
      }),
      transformResponse: (response: { meta: IGenericSuccessResponse }) => {
        return response?.meta;
      },
      invalidatesTags: ["CartGroup", "Carts", "SharedClients"],
    }),

    getSharedGroupCartUsers: builder.query<Array<IClient>, string>({
      query: (id) => ({
        url: `/api/v1/share-cart/shared-user-list?cart_group_id=${id}`,
      }),
      providesTags: ["SharedClients"],
    }),
  }),
});

export const {
  useGetCartsQuery,
  useLazyGetCartsQuery,
  useGetCartGroupsQuery,
  useLazyGetCartGroupsQuery,
  useGetCartByIdQuery,
  useDeleteCartMutation,
  useDeleteCartGroupMutation,
  useDeleteRecurringPaymentMutation,
  useDeleteTemplateMutation,
  useCreateCartGroupMutation,
  useEditCartGroupMutation,
  useGetCartGroupByIdQuery,
  useGetCartsByCartGroupIdQuery,
  useAddServiceToCartMutation,
  useUpdateCartMutation,
  useDelayPaymentMutation,
  useUndelayPaymentMutation,
  useAddRecurringPaymentMutation,
  useAddTemplateMutation,
  useLazyGetRecurringPaymentQuery,
  useGetRecurringPaymentsQuery,
  useLazyGetTemplateQuery,
  useAddTemplateToCartMutation,
  useEditTemplateMutation,
  useGetTemplatesQuery,
  useGetTemplateQuery,
  usePaynowTemplateMutation,
  usePayCashMockMutation,
  useLazyReloadCartByIdQuery,
  useShareCartMutation,
  useShareCartConfirmMutation,
  useShareCartRefuseMutation,
  useCancelSharedCartMutation,
  useGetSharedGroupCartUsersQuery,
  useEditSharedCartPermissionsMutation,
  useGetCartGroupsWithTrashQuery,
} = cartApi;

export default cartApi.reducer;
